/* eslint-disable no-alert */
/* eslint-disable no-unused-expressions */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Drawer,
  Grid,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  AddIcon,
  DeleteForeverIcon,
  EditIcon,
  ExpandIcon,
} from 'src/v3/core/icons'

import * as yup from 'yup'
import { ReactElement, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchLiveClassTemplates,
  selectLiveClassTemplates,
  selectLiveClassTemplatesStatus,
} from 'src/v3/admin/admin.slice'

import { useFormik } from 'formik'
import axios from 'src/utilities/http-client'
import { compose, F, T } from 'ramda'
import ConfirmAction from 'src/v3/components/confirm-action'

export default (): ReactElement => {
  let dispatch = useAppDispatch()

  let templates = useAppSelector(selectLiveClassTemplates)
  let templatesStatus = useAppSelector(selectLiveClassTemplatesStatus)
  // let courses = useAppSelector(selectCourses)
  // let coursesStatus = useAppSelector(selectCoursesStatus)

  let [errorMessage, setErrorMessage] = useState<string>('')
  let [saving, setSaving] = useState<boolean>(false)
  let [deleting, setDeleting] = useState<string>('')
  let [formOpen, setFormOpen] = useState<boolean>(false)

  let form = useFormik({
    validationSchema: yup.object({
      name: yup.string().required(),
      statement1: yup.string().required(),
      statement2: yup.string().required(),
    }),
    enableReinitialize: true,
    initialValues: {
      id: '',
      name: '',
      statement1: '',
      statement2: '',
    },
    onSubmit: (values) => {
      setSaving(true)
      if (!values.id) {
        axios
          .post('/api/v2/admin/live-class/template', {
            name: values.name,
            statement1: values.statement1,
            statement2: values.statement2,
          })
          .then(() => dispatch(fetchLiveClassTemplates()))
          .then(() => setFormOpen(false))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(err?.message || 'Error Creating Template')
            dispatch(fetchLiveClassTemplates())
            setFormOpen(false)
            setSaving(false)
          })
      } else {
        axios
          .put('/api/v2/admin/live-class/template', {
            id: values.id,
            name: values.name,
            statement1: values.statement1,
            statement2: values.statement2,
          })
          .then(() => dispatch(fetchLiveClassTemplates()))
          .then(() => setFormOpen(false))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(err?.message || 'Error Updating Template')
            dispatch(fetchLiveClassTemplates())
            setFormOpen(false)
            setSaving(false)
          })
      }
    },
  })

  useEffect(() => {
    if (templatesStatus === 'uninitialized') {
      dispatch(fetchLiveClassTemplates())
    }
  }, [templatesStatus])

  // useEffect(() => {
  //   if (coursesStatus === 'uninitialized') {
  //     dispatch(fetchCourses())
  //   }
  // }, [coursesStatus])

  useEffect(() => {
    if (!formOpen) {
      form.resetForm()
    }
  }, [formOpen])

  let onEdit = (id: string) => {
    let template = templates.find((t) => t.id === id)
    form.setFieldValue('id', template?.id)
    form.setFieldValue('name', template?.name)
    form.setFieldValue('statement1', template?.statement1)
    form.setFieldValue('statement2', template?.statement2)
    form.setFieldValue('linkedService', template?.linkedServiceID)
    setFormOpen(true)
  }

  return (
    <>
      <Box component="main" sx={{ p: 3 }}>
        <Typography
          variant="h4"
          className="my-4 d-inline"
          style={{
            fontFamily: 'smileAndWave',
          }}
        >
          Templates
        </Typography>
        <Typography paragraph className="d-inline ml-3">
          Create a template or select one to edit
        </Typography>
        <div>
          <Button
            className="mt-3"
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={compose(setFormOpen, T)}
          >
            Add New Template
          </Button>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {templates.map((t, i) => (
              <Grid key={`${t.name}-${i}`} item xl={4} md={6} xs={12}>
                <Paper
                  sx={{
                    p: 2,
                    margin: 'auto',
                    flexGrow: 1,
                    boxShadow: 'inset 0 0 0 1000px rgba(37, 43, 66, 0.94)',
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm container>
                      <Grid item xs container direction="column" spacing={2}>
                        <Grid item xs>
                          <Typography
                            gutterBottom
                            variant="subtitle1"
                            component="div"
                          >
                            {t.name}
                          </Typography>

                          <Accordion disableGutters>
                            <AccordionSummary expandIcon={<ExpandIcon />}>
                              Accrediting Agency 1 Statement
                            </AccordionSummary>
                            <AccordionDetails
                              className="px-3 pt-0 flex justify-content-between"
                              sx={{
                                gap: 1,
                                flexWrap: 'wrap',
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {t.statement1 || 'None'}
                              </Typography>
                            </AccordionDetails>
                          </Accordion>
                          <Accordion disableGutters>
                            <AccordionSummary expandIcon={<ExpandIcon />}>
                              Accrediting Agency 2 Statement
                            </AccordionSummary>
                            <AccordionDetails
                              className="px-3 pt-0 flex justify-content-between"
                              sx={{
                                gap: 1,
                                flexWrap: 'wrap',
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {t.statement2 || 'None'}
                              </Typography>
                            </AccordionDetails>
                          </Accordion>
                        </Grid>
                        <Grid item>
                          <div className="flex flex-row-reverse">
                            <Tooltip title="Delete">
                              <Button
                                variant="outlined"
                                className="ml-3"
                                onClick={() => setDeleting(`delete-${t.id}`)}
                                disabled={
                                  templates.length === 1 // ||
                                  // courses.some(c => c.templateID === t.id)
                                }
                              >
                                <DeleteForeverIcon />
                              </Button>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <Button
                                variant="outlined"
                                className="ml-3"
                                onClick={() => onEdit(t.id)}
                              >
                                <EditIcon />
                              </Button>
                            </Tooltip>
                          </div>
                        </Grid>
                        <ConfirmAction
                          executeAction={() =>
                            axios
                              .delete(
                                `/api/v2/admin/live-class/delete-template?templateID=${t.id}`,
                              )
                              .then(() => dispatch(fetchLiveClassTemplates()))
                          }
                          open={deleting}
                          setOpen={setDeleting}
                          openText={`delete-${t.id}`}
                          title="Confirm Deletion"
                          text={`Are you sure you want to delete ${t.name}?`}
                          onFailure={(err) =>
                            setErrorMessage(
                              err.message || 'Error Deleting Template',
                            )
                          }
                        />
                      </Grid>
                      <Grid item>{/* Nothing here */}</Grid>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </div>

        <Drawer
          anchor="bottom"
          open={formOpen}
          onClose={compose(setFormOpen, F)}
        >
          <Container maxWidth="sm">
            <Card className="m-4">
              <form className="p-4" onSubmit={form.handleSubmit}>
                <TextField
                  fullWidth
                  className="mt-4"
                  variant="standard"
                  value={form.values.name}
                  label="Name"
                  name="name"
                  onChange={form.handleChange}
                  error={form.touched.name && !!form.errors.name}
                />
                <TextField
                  fullWidth
                  className="mt-4"
                  variant="standard"
                  value={form.values.statement1}
                  label="Accrediting Agency 1 Statement*"
                  name="statement1"
                  onChange={form.handleChange}
                  error={form.touched.statement1 && !!form.errors.statement1}
                />
                <TextField
                  fullWidth
                  className="mt-4"
                  variant="standard"
                  value={form.values.statement2}
                  label="Accrediting Agency 2 Statement*"
                  name="statement2"
                  onChange={form.handleChange}
                  error={form.touched.statement2 && !!form.errors.statement2}
                />
                <Typography variant="subtitle2" className="mt-4">
                  *In an accredidation statement that includes the length of the
                  course, enter "$HOUR" and the length of the course will be
                  inserted in the certificate.
                </Typography>
                <div className="flex flex-row-reverse">
                  {saving ? (
                    <CircularProgress className="mt-4" />
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      className="mt-4 mr-3"
                      type="submit"
                    >
                      Submit
                    </Button>
                  )}
                </div>
              </form>
            </Card>
          </Container>
        </Drawer>
      </Box>
      <Backdrop open={templatesStatus === 'pending'}>
        <CircularProgress />
      </Backdrop>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  )
}
